import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { bookingsService, addonsService, itemsService, bookingAddonsService, priceBreakdownsService } from '../services/api';
import { PERMISSIONS, hasPermission } from '../utils/rbac';
import { useIsMobile } from '../hooks';
import { ConciergeBell as ConciergeBellIcon, Hotel as HotelIcon, Hourglass as HourglassIcon, Package as PackageIcon } from 'lucide-react';

/**
 * ServicesBooking Component
 * Multi-step flow for creating booking addons with item selection
 * Flow: Check-in Bookings → Select Service → Select Items → Confirm → Create
 */
function ServicesBooking({ user }) {
  const isMobile = useIsMobile();
  // Main state
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Bookings, 2: Services, 3: Items, 4: Confirmation, 5: Invoice
  
  // Step 1: Bookings list
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Step 2: Services list
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  
  // Step 3: Items list
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]); // [{itemId, name, quantity, unitPrice}]
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  
  // Step 4: Confirmation
  const [creating, setCreating] = useState(false);
  const [serviceBreakdowns, setServiceBreakdowns] = useState([]);
  const invoiceRef = useRef(null);

  // Access control - Check if user has permission to read add-ons (services)
  const hasAccess = hasPermission(user?.role, PERMISSIONS.ADDON_READ);

  useEffect(() => {
    if (hasAccess && step === 1) {
      loadCheckedInBookings();
    }
  }, [hasAccess, step]);

  // Load checked-in bookings
  const loadCheckedInBookings = async () => {
    setLoading(true);
    try {
      const pageSize = 100;
      let currentPage = 1;
      let totalPages = 1;
      let allBookings = [];
      const responseSignatures = new Set();

      const extractBookings = (apiResponse) => {
        if (apiResponse?.data?.bookings && Array.isArray(apiResponse.data.bookings)) {
          return apiResponse.data.bookings;
        }
        if (apiResponse?.data && Array.isArray(apiResponse.data)) {
          return apiResponse.data;
        }
        if (Array.isArray(apiResponse)) {
          return apiResponse;
        }
        return [];
      };

      const extractPagination = (apiResponse) => {
        if (apiResponse?.pagination) {
          return apiResponse.pagination;
        }
        if (apiResponse?.data?.pagination) {
          return apiResponse.data.pagination;
        }
        return null;
      };

      while (currentPage <= totalPages) {
        const response = await bookingsService.getAll({
          status: 'CHECKED_IN',
          page: currentPage,
          limit: pageSize,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        });

        const pageBookings = extractBookings(response);
        const pagePagination = extractPagination(response);

        allBookings = allBookings.concat(pageBookings);

        if (pagePagination?.totalPages && Number(pagePagination.totalPages) > 0) {
          totalPages = Number(pagePagination.totalPages);
        } else {
          totalPages = pageBookings.length === pageSize ? currentPage + 1 : currentPage;

          const pageSignature = `${pageBookings.length}-${pageBookings.map(b => b?.id).filter(Boolean).join('|')}`;
          if (responseSignatures.has(pageSignature)) {
            break;
          }
          responseSignatures.add(pageSignature);
        }

        currentPage += 1;
      }

      setBookings(allBookings);
    } catch (error) {
      console.error('Error loading bookings:', error);
      toast.error('Failed to load checked-in bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  // Load addon services
  const loadServices = async () => {
    setLoading(true);
    try {
      const response = await addonsService.getAll({ limit: 100 });
      
      let servicesList = [];
      if (response?.data?.addOns && Array.isArray(response.data.addOns)) {
        servicesList = response.data.addOns;
      } else if (response?.data && Array.isArray(response.data)) {
        servicesList = response.data;
      } else if (Array.isArray(response)) {
        servicesList = response;
      }
      
      setServices(servicesList);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Failed to load addon services');
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // Load items for selected service
  const loadItems = async (serviceId) => {
    setLoading(true);
    try {
      const response = await itemsService.getByService(serviceId);
      
      let itemsList = [];
      if (response?.data?.items && Array.isArray(response.data.items)) {
        itemsList = response.data.items.filter(item => item.isActive && item.quantity > 0);
      } else if (response?.data && Array.isArray(response.data)) {
        itemsList = response.data.filter(item => item.isActive && item.quantity > 0);
      } else if (Array.isArray(response)) {
        itemsList = response.filter(item => item.isActive && item.quantity > 0);
      }
      
      setItems(itemsList);
      
      if (itemsList.length === 0) {
        toast.warning('No available items for this service');
      }
    } catch (error) {
      console.error('Error loading items:', error);
      toast.error('Failed to load service items');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle booking selection
  const handleSelectBooking = (booking) => {
    setSelectedBooking(booking);
    setStep(2);
    loadServices();
  };

  // Handle service selection
  const handleSelectService = (service) => {
    setSelectedService(service);
    setStep(3);
    loadItems(service.id);
    loadServiceBreakdowns(service.id);
  };

  // Load price breakdowns for selected service
  const loadServiceBreakdowns = async (serviceId) => {
    try {
      const response = await priceBreakdownsService.getAddOnServiceBreakdowns(serviceId, false);
      setServiceBreakdowns(response?.data?.priceBreakdowns || []);
    } catch (error) {
      console.error('Error loading price breakdowns:', error);
      setServiceBreakdowns([]);
    }
  };

  // Download invoice as PDF
  const handleDownloadInvoice = async () => {
    if (!invoiceRef.current) return;
    try {
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`invoice-${selectedBooking?.bookingNumber || 'service'}-${Date.now()}.pdf`);
      toast.success('Invoice downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to download invoice');
    }
  };

  // Handle item quantity change
  const handleItemQuantityChange = (item, quantity) => {
    const qty = parseInt(quantity) || 0;
    
    if (qty > item.quantity) {
      toast.error(`Only ${item.quantity} units available in stock`);
      return;
    }
    
    setSelectedItems(prev => {
      const existing = prev.find(i => i.itemId === item.id);
      
      if (qty === 0) {
        // Remove item if quantity is 0
        return prev.filter(i => i.itemId !== item.id);
      }
      
      if (existing) {
        // Update existing item
        return prev.map(i => 
          i.itemId === item.id 
            ? { ...i, quantity: qty, totalPrice: qty * item.unitPrice }
            : i
        );
      } else {
        // Add new item
        return [...prev, {
          itemId: item.id,
          name: item.name,
          sku: item.sku,
          quantity: qty,
          unitPrice: item.unitPrice,
          totalPrice: qty * item.unitPrice
        }];
      }
    });
  };

  // Get item quantity from selected items
  const getItemQuantity = (itemId) => {
    const item = selectedItems.find(i => i.itemId === itemId);
    return item ? item.quantity : 0;
  };

  // Calculate total price
  const calculateTotal = () => {
    return selectedItems.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  // Handle create addon
  const handleCreateAddon = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }

    setCreating(true);
    try {
      // Create separate booking addon for each item
      // API requires one addon per call, not an items array
      const createdAddons = [];
      
      for (const item of selectedItems) {
        const addonData = {
          bookingId: selectedBooking.id,
          addOnServiceId: selectedService.id,
          itemId: item.itemId,
          quantity: item.quantity,
          price: item.unitPrice  // Per-unit price (required)
        };

        console.log('Creating addon:', addonData);
        const response = await bookingAddonsService.create(addonData);
        createdAddons.push(response.data);
      }
      
      toast.success(`${createdAddons.length} addon service${createdAddons.length > 1 ? 's' : ''} created successfully!`);
      
      // Move to invoice step
      setStep(5);
    } catch (error) {
      console.error('Error creating addon:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create addon service';
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setCreating(false);
    }
  };

  // Reset flow
  const resetFlow = () => {
    setStep(1);
    setSelectedBooking(null);
    setSelectedService(null);
    setSelectedItems([]);
    setServiceBreakdowns([]);
    loadCheckedInBookings();
  };

  // Go back to previous step
  const handleBack = () => {
    if (step === 2) {
      setSelectedBooking(null);
      setSelectedService(null);
      setStep(1);
    } else if (step === 3) {
      setSelectedService(null);
      setSelectedItems([]);
      setStep(2);
      loadServices();
    } else if (step === 4) {
      setStep(3);
    } else if (step === 5) {
      setStep(4);
    }
  };

  // Filter bookings by search
  const filteredBookings = bookings.filter(booking => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    const bookingNumber = booking.bookingNumber?.toLowerCase() || '';
    const guestName = booking.user?.name?.toLowerCase() || '';
    const guestEmail = booking.user?.email?.toLowerCase() || '';
    
    return bookingNumber.includes(search) || 
           guestName.includes(search) || 
           guestEmail.includes(search);
  });

  const filteredServices = services.filter(service => {
    if (!serviceSearchTerm) return true;
    const search = serviceSearchTerm.toLowerCase();
    return (service.name || '').toLowerCase().includes(search) ||
      (service.description || '').toLowerCase().includes(search);
  });

  const filteredItems = items.filter(item => {
    if (!itemSearchTerm) return true;
    const search = itemSearchTerm.toLowerCase();
    return (item.name || '').toLowerCase().includes(search) ||
      (item.sku || '').toLowerCase().includes(search) ||
      (item.description || '').toLowerCase().includes(search);
  });

  if (!hasAccess) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with progress indicator */}
      <div style={{
        padding: isMobile ? '16px' : '24px',
        borderBottom: '2px solid #E3DFD3',
        backgroundColor: 'white'
      }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: isMobile ? '20px' : '24px', fontWeight: '700', color: '#1A3E2A' }}>
          Services & Add-ons
        </h2>
        
        {/* Progress Steps */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px', overflowX: isMobile ? 'auto' : 'visible', paddingBottom: isMobile ? '6px' : '0' }}>
          {[
            { num: 1, label: 'Select Booking' },
            { num: 2, label: 'Choose Service' },
            { num: 3, label: 'Select Items' },
            { num: 4, label: 'Confirm' },
            { num: 5, label: 'Invoice' }
          ].map((s, index) => (
            <React.Fragment key={s.num}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: isMobile ? '8px 12px' : '8px 16px',
                borderRadius: '8px',
                backgroundColor: step >= s.num ? '#1A3E2A' : '#EEEBE1',
                color: step >= s.num ? 'white' : '#5F6B61',
                fontWeight: '600',
                fontSize: isMobile ? '12px' : '14px',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}>
                <span style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: step >= s.num ? 'rgba(255,255,255,0.3)' : '#E3DFD3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: '700'
                }}>
                  {s.num}
                </span>
                {s.label}
              </div>
              {index < 3 && (
                <div style={{
                  flex: 1,
                  height: '2px',
                  backgroundColor: step > s.num ? '#1A3E2A' : '#E3DFD3',
                  transition: 'all 0.3s ease'
                }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Breadcrumb */}
        {step > 1 && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#F5F3EB',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#5F6B61',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            {selectedBooking && (
              <span>
                <strong>Booking:</strong> {selectedBooking.bookingNumber} - {selectedBooking.user?.name}
              </span>
            )}
            {selectedService && (
              <span style={{ marginLeft: isMobile ? '0' : '16px' }}>
                <strong>Service:</strong> {selectedService.name}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? '16px' : '24px', backgroundColor: '#F5F3EB' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
            <p style={{ fontSize: '16px', color: '#5F6B61' }}>Loading...</p>
          </div>
        ) : (
          <>
            {/* Step 1: Select Booking */}
            {step === 1 && (
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'stretch' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                    Checked-in Bookings ({filteredBookings.length})
                  </h3>
                  <input
                    type="text"
                    placeholder="Search by booking number, guest name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      padding: '10px 16px',
                      border: '1px solid #E3DFD3',
                      borderRadius: '8px',
                      fontSize: '14px',
                      width: isMobile ? '100%' : '400px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {filteredBookings.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '48px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '2px dashed #E3DFD3'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}><HotelIcon size="1em" aria-hidden="true" /></div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1A3E2A' }}>
                      No Checked-in Bookings
                    </h3>
                    <p style={{ margin: 0, color: '#5F6B61' }}>
                      {searchTerm ? 'No bookings match your search' : 'No guests are currently checked in'}
                    </p>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))',
                    gap: '16px'
                  }}>
                    {filteredBookings.map(booking => (
                      <BookingCard 
                        key={booking.id} 
                        booking={booking} 
                        onSelect={handleSelectBooking}
                        isMobile={isMobile}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Select Service */}
            {step === 2 && (
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'stretch' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                    Select Add-on Service ({filteredServices.length})
                  </h3>
                  <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
                    <input
                      type="text"
                      placeholder="Search services..."
                      value={serviceSearchTerm}
                      onChange={(e) => setServiceSearchTerm(e.target.value)}
                      style={{
                        padding: '10px 16px',
                        border: '1px solid #E3DFD3',
                        borderRadius: '8px',
                        fontSize: '14px',
                        width: isMobile ? '100%' : '280px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <button
                      onClick={handleBack}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#5F6B61',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      ← Back
                    </button>
                  </div>
                </div>

                {filteredServices.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '48px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '2px dashed #E3DFD3'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}><ConciergeBellIcon size="1em" aria-hidden="true" /></div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1A3E2A' }}>
                      No Services Available
                    </h3>
                    <p style={{ margin: 0, color: '#5F6B61' }}>
                      Create addon services in Add-ons Management first
                    </p>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '16px'
                  }}>
                    {filteredServices.map(service => (
                      <ServiceCard 
                        key={service.id} 
                        service={service} 
                        onSelect={handleSelectService}
                        isMobile={isMobile}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Select Items */}
            {step === 3 && (
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'stretch' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                    Select Items & Quantity ({filteredItems.length} available)
                  </h3>
                  <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={itemSearchTerm}
                      onChange={(e) => setItemSearchTerm(e.target.value)}
                      style={{
                        padding: '10px 16px',
                        border: '1px solid #E3DFD3',
                        borderRadius: '8px',
                        fontSize: '14px',
                        width: isMobile ? '100%' : '260px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <button
                      onClick={handleBack}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#5F6B61',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      ← Back
                    </button>
                    <button
                      onClick={() => setStep(4)}
                      disabled={selectedItems.length === 0}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: selectedItems.length > 0 ? '#1A3E2A' : '#D2CDBF',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: selectedItems.length > 0 ? 'pointer' : 'not-allowed'
                      }}
                    >
                      Continue →
                    </button>
                  </div>
                </div>

                {filteredItems.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '48px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '2px dashed #E3DFD3'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}><PackageIcon size="1em" aria-hidden="true" /></div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1A3E2A' }}>
                      No Items Available
                    </h3>
                    <p style={{ margin: 0, color: '#5F6B61' }}>
                      No items are available for this service or all items are out of stock
                    </p>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '16px'
                  }}>
                    {filteredItems.map(item => (
                      <ItemCard 
                        key={item.id} 
                        item={item}
                        quantity={getItemQuantity(item.id)}
                        onQuantityChange={handleItemQuantityChange}
                        isMobile={isMobile}
                      />
                    ))}
                  </div>
                )}

                {/* Selected Items Summary */}
                {selectedItems.length > 0 && (
                  <div style={{
                    marginTop: '24px',
                    padding: isMobile ? '16px' : '20px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '2px solid #1A3E2A'
                  }}>
                    <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
                      Selected Items ({selectedItems.length})
                    </h4>
                    {selectedItems.map(item => (
                      <div key={item.itemId} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? '6px' : '0',
                        padding: '8px 0',
                        borderBottom: '1px solid #EEEBE1'
                      }}>
                        <span style={{ fontSize: '14px', color: '#36433B' }}>
                          {item.name} ({item.sku})
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A', textAlign: isMobile ? 'left' : 'right', width: isMobile ? '100%' : 'auto' }}>
                          {item.quantity} × ₹{item.unitPrice.toFixed(2)} = ₹{item.totalPrice.toFixed(2)}
                        </span>
                      </div>
                    ))}
                    <div style={{
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: '2px solid #E3DFD3',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? '6px' : '0'
                    }}>
                      <span style={{ fontSize: '16px', fontWeight: '700', color: '#1A3E2A' }}>
                        Total Amount
                      </span>
                      <span style={{ fontSize: '20px', fontWeight: '700', color: '#1A3E2A' }}>
                        ₹{calculateTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Confirmation */}
            {step === 4 && (
              <div>
                <div style={{
                  maxWidth: '800px',
                  margin: '0 auto',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: isMobile ? '20px 16px' : '32px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{
                    margin: '0 0 24px 0',
                    fontSize: isMobile ? '20px' : '24px',
                    fontWeight: '700',
                    textAlign: 'center',
                    color: '#1A3E2A'
                  }}>
                    Confirm Service
                  </h3>

                  {/* Booking Details */}
                  <div style={{
                    marginBottom: '24px',
                    padding: '16px',
                    backgroundColor: '#F5F3EB',
                    borderRadius: '8px'
                  }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                      Booking Details
                    </h4>
                    <div style={{ fontSize: '14px', color: '#5F6B61', lineHeight: '1.6' }}>
                      <p style={{ margin: '4px 0' }}>
                        <strong>Booking Number:</strong> {selectedBooking.bookingNumber}
                      </p>
                      <p style={{ margin: '4px 0' }}>
                        <strong>Guest:</strong> {selectedBooking.user?.name}
                      </p>
                      <p style={{ margin: '4px 0' }}>
                        <strong>Email:</strong> {selectedBooking.user?.email}
                      </p>
                    </div>
                  </div>

                  {/* Service Details */}
                  <div style={{
                    marginBottom: '24px',
                    padding: '16px',
                    backgroundColor: '#F5F3EB',
                    borderRadius: '8px'
                  }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                      Service Details
                    </h4>
                    <div style={{ fontSize: '14px', color: '#5F6B61', lineHeight: '1.6' }}>
                      <p style={{ margin: '4px 0' }}>
                        <strong>Service:</strong> {selectedService.name}
                      </p>
                      {selectedService.description && (
                        <p style={{ margin: '4px 0' }}>
                          <strong>Description:</strong> {selectedService.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Items Details */}
                  <div style={{
                    marginBottom: '24px',
                    padding: '16px',
                    backgroundColor: '#F5F3EB',
                    borderRadius: '8px'
                  }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                      Selected Items
                    </h4>
                    {selectedItems.map((item, index) => (
                      <div key={item.itemId} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? '8px' : '0',
                        padding: '12px 0',
                        borderBottom: index < selectedItems.length - 1 ? '1px solid #E3DFD3' : 'none'
                      }}>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
                            {item.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#5F6B61' }}>
                            SKU: {item.sku}
                          </div>
                        </div>
                        <div style={{ textAlign: isMobile ? 'left' : 'right', width: isMobile ? '100%' : 'auto' }}>
                          <div style={{ fontSize: '14px', color: '#36433B' }}>
                            Qty: {item.quantity} × ₹{item.unitPrice.toFixed(2)}
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
                            ₹{item.totalPrice.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <div style={{
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: '2px solid #E3DFD3',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? '6px' : '0'
                    }}>
                      <span style={{ fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                        Base Total
                      </span>
                      <span style={{ fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                        ₹{calculateTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Price Breakdowns */}
                  {serviceBreakdowns.filter(b => b.isActive !== false).length > 0 && (
                    <div style={{
                      marginBottom: '24px',
                      padding: '16px',
                      backgroundColor: '#F5F3EB',
                      borderRadius: '8px'
                    }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                        Price Breakdown
                      </h4>
                      {serviceBreakdowns.filter(b => b.isActive !== false).map((breakdown) => {
                        const baseTotal = calculateTotal();
                        const breakdownAmount = breakdown.calculationType === 'PERCENTAGE'
                          ? (baseTotal * (breakdown.value || 0)) / 100
                          : (breakdown.value || breakdown.amount || 0);
                        return (
                          <div
                            key={breakdown.id}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '10px 0',
                              borderBottom: '1px solid #E3DFD3'
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: '#24332A' }}>
                                {breakdown.name}
                              </div>
                              <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '2px' }}>
                                <span style={{ 
                                  backgroundColor: '#E3DFD3', 
                                  padding: '2px 8px', 
                                  borderRadius: '4px',
                                  marginRight: '8px'
                                }}>
                                  {breakdown.breakdownType}
                                </span>
                                <span>
                                  {breakdown.calculationType === 'PERCENTAGE' 
                                    ? `${breakdown.value}%` 
                                    : `₹${breakdown.value}`
                                  }
                                </span>
                              </div>
                            </div>
                            <span style={{ 
                              fontSize: '15px', 
                              fontWeight: '600',
                              color: breakdown.breakdownType === 'DISCOUNT' ? '#163626' : '#8E3628'
                            }}>
                              {breakdown.breakdownType === 'DISCOUNT' ? '-' : '+'}₹{Math.abs(breakdownAmount).toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Grand Total */}
                  <div style={{
                    marginBottom: '32px',
                    padding: '16px',
                    backgroundColor: '#F0F3EE',
                    borderRadius: '8px',
                    border: '2px solid #1A3E2A',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '6px' : '0'
                  }}>
                    <span style={{ fontSize: '18px', fontWeight: '700', color: '#1A3E2A' }}>
                      Total Amount
                    </span>
                    <span style={{ fontSize: isMobile ? '22px' : '24px', fontWeight: '700', color: '#1A3E2A' }}>
                      ₹{calculateTotal().toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ maxWidth: '800px', margin: '24px auto 0', display: 'flex', gap: '12px', justifyContent: 'center', flexDirection: isMobile ? 'column' : 'row' }}>
                  <button
                    onClick={handleBack}
                    disabled={creating}
                    style={{
                      padding: '12px 32px',
                      backgroundColor: '#5F6B61',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: creating ? 'not-allowed' : 'pointer',
                      opacity: creating ? 0.5 : 1,
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    ← Back
                  </button>
                  <button
                    onClick={handleCreateAddon}
                    disabled={creating}
                    style={{
                      padding: '12px 32px',
                      backgroundColor: creating ? '#D2CDBF' : '#1A3E2A',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: creating ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'center',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    {creating ? (
                      <>
                        <span style={{ animation: 'spin 1s linear infinite' }}><HourglassIcon size="1em" aria-hidden="true" /></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        ✓ Confirm & Create
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step 5: Invoice (only after confirmation) */}
            {step === 5 && (
              <div>
                <div ref={invoiceRef} style={{
                  maxWidth: '800px',
                  margin: '0 auto',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: isMobile ? '20px 16px' : '32px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{
                    margin: '0 0 24px 0',
                    fontSize: isMobile ? '20px' : '24px',
                    fontWeight: '700',
                    textAlign: 'center',
                    color: '#1A3E2A'
                  }}>
                    Invoice
                  </h3>

                  {/* Booking Details */}
                  <div style={{
                    marginBottom: '24px',
                    padding: '16px',
                    backgroundColor: '#F5F3EB',
                    borderRadius: '8px'
                  }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                      Booking Details
                    </h4>
                    <div style={{ fontSize: '14px', color: '#5F6B61', lineHeight: '1.6' }}>
                      <p style={{ margin: '4px 0' }}>
                        <strong>Booking Number:</strong> {selectedBooking?.bookingNumber}
                      </p>
                      <p style={{ margin: '4px 0' }}>
                        <strong>Guest:</strong> {selectedBooking?.user?.name}
                      </p>
                      <p style={{ margin: '4px 0' }}>
                        <strong>Email:</strong> {selectedBooking?.user?.email}
                      </p>
                    </div>
                  </div>

                  {/* Service Details */}
                  <div style={{
                    marginBottom: '24px',
                    padding: '16px',
                    backgroundColor: '#F5F3EB',
                    borderRadius: '8px'
                  }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                      Service Details
                    </h4>
                    <div style={{ fontSize: '14px', color: '#5F6B61', lineHeight: '1.6' }}>
                      <p style={{ margin: '4px 0' }}>
                        <strong>Service:</strong> {selectedService?.name}
                      </p>
                      {selectedService?.description && (
                        <p style={{ margin: '4px 0' }}>
                          <strong>Description:</strong> {selectedService.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Items Details */}
                  <div style={{
                    marginBottom: '24px',
                    padding: '16px',
                    backgroundColor: '#F5F3EB',
                    borderRadius: '8px'
                  }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                      Selected Items
                    </h4>
                    {selectedItems.map((item, index) => (
                      <div key={item.itemId} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? '8px' : '0',
                        padding: '12px 0',
                        borderBottom: index < selectedItems.length - 1 ? '1px solid #E3DFD3' : 'none'
                      }}>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
                            {item.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#5F6B61' }}>
                            SKU: {item.sku}
                          </div>
                        </div>
                        <div style={{ textAlign: isMobile ? 'left' : 'right', width: isMobile ? '100%' : 'auto' }}>
                          <div style={{ fontSize: '14px', color: '#36433B' }}>
                            Qty: {item.quantity} × ₹{item.unitPrice.toFixed(2)}
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
                            ₹{item.totalPrice.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <div style={{
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: '2px solid #E3DFD3',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? '6px' : '0'
                    }}>
                      <span style={{ fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                        Base Total
                      </span>
                      <span style={{ fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                        ₹{calculateTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Price Breakdowns */}
                  {serviceBreakdowns.filter(b => b.isActive !== false).length > 0 && (
                    <div style={{
                      marginBottom: '24px',
                      padding: '16px',
                      backgroundColor: '#F5F3EB',
                      borderRadius: '8px'
                    }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
                        Price Breakdown
                      </h4>
                      {serviceBreakdowns.filter(b => b.isActive !== false).map((breakdown) => {
                        const baseTotal = calculateTotal();
                        const breakdownAmount = breakdown.calculationType === 'PERCENTAGE'
                          ? (baseTotal * (breakdown.value || 0)) / 100
                          : (breakdown.value || breakdown.amount || 0);
                        return (
                          <div
                            key={breakdown.id}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '10px 0',
                              borderBottom: '1px solid #E3DFD3'
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: '#24332A' }}>
                                {breakdown.name}
                              </div>
                              <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '2px' }}>
                                <span style={{ 
                                  backgroundColor: '#E3DFD3', 
                                  padding: '2px 8px', 
                                  borderRadius: '4px',
                                  marginRight: '8px'
                                }}>
                                  {breakdown.breakdownType}
                                </span>
                                <span>
                                  {breakdown.calculationType === 'PERCENTAGE' 
                                    ? `${breakdown.value}%` 
                                    : `₹${breakdown.value}`
                                  }
                                </span>
                              </div>
                            </div>
                            <span style={{ 
                              fontSize: '15px', 
                              fontWeight: '600',
                              color: breakdown.breakdownType === 'DISCOUNT' ? '#163626' : '#8E3628'
                            }}>
                              {breakdown.breakdownType === 'DISCOUNT' ? '-' : '+'}₹{Math.abs(breakdownAmount).toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Grand Total */}
                  <div style={{
                    marginBottom: '32px',
                    padding: '16px',
                    backgroundColor: '#F0F3EE',
                    borderRadius: '8px',
                    border: '2px solid #1A3E2A',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '6px' : '0'
                  }}>
                    <span style={{ fontSize: '18px', fontWeight: '700', color: '#1A3E2A' }}>
                      Total Amount
                    </span>
                    <span style={{ fontSize: isMobile ? '22px' : '24px', fontWeight: '700', color: '#1A3E2A' }}>
                      ₹{calculateTotal().toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Action Buttons (outside invoiceRef so they don't appear in PDF) */}
                <div style={{ maxWidth: '800px', margin: '24px auto 0', display: 'flex', gap: '12px', justifyContent: 'center', flexDirection: isMobile ? 'column' : 'row' }}>
                  <button
                    onClick={handleDownloadInvoice}
                    style={{
                      padding: '12px 32px',
                      backgroundColor: '#163626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'center',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    Download Invoice
                  </button>
                  <button
                    onClick={resetFlow}
                    style={{
                      padding: '12px 32px',
                      backgroundColor: '#1A3E2A',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'center',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    ✓ Done
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Booking Card Component
function BookingCard({ booking, onSelect, isMobile }) {
  // Get accommodation details
  const room = booking.bookedRooms?.[0];
  const bed = booking.bookedBeds?.[0];
  const course = booking.bookedCourses?.[0];
  
  // Check if booking is from BOOKING_ENGINE
  const isBookingEngine = booking.bookingPlatform === 'BOOKING_ENGINE';

  return (
    <div
      onClick={() => onSelect(booking)}
      style={{
        padding: '20px',
        backgroundColor: isBookingEngine ? '#E1E9E2' : 'white', // Light blue shade for booking engine bookings
        borderRadius: '12px',
        border: isBookingEngine ? '2px solid #9DB7A5' : '2px solid #E3DFD3',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        ':hover': {
          borderColor: '#1A3E2A',
          boxShadow: '0 4px 12px rgba(26, 62, 42, 0.15)'
        }
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#1A3E2A';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(26, 62, 42, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = isBookingEngine ? '#9DB7A5' : '#E3DFD3';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1A3E2A', paddingRight: '8px', wordBreak: 'break-word' }}>
          {booking.bookingNumber}
        </h4>
        <span style={{
          padding: '4px 12px',
          backgroundColor: '#1A3E2A',
          color: 'white',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: '600',
          flexShrink: 0
        }}>
          CHECKED-IN
        </span>
      </div>

      <div style={{ fontSize: '14px', color: '#5F6B61', lineHeight: '1.6', wordBreak: 'break-word' }}>
        <p style={{ margin: '4px 0' }}>
          <strong>Guest:</strong> {booking.user?.name || 'N/A'}
        </p>
        <p style={{ margin: '4px 0' }}>
          <strong>Email:</strong> {booking.user?.email || 'N/A'}
        </p>
        {room && (
          <p style={{ margin: '4px 0' }}>
            <strong>Room:</strong> {room.roomNumber}
          </p>
        )}
        {bed && (
          <p style={{ margin: '4px 0' }}>
            <strong>Bed:</strong> {bed.bedNumber} (Room: {bed.roomNumber})
          </p>
        )}
        {course && (
          <p style={{ margin: '4px 0' }}>
            <strong>Course:</strong> {course.courseName}
          </p>
        )}
        <p style={{ margin: '4px 0' }}>
          <strong>Check-in:</strong> {new Date(booking.checkIn).toLocaleDateString()}
        </p>
      </div>

      <button
        style={{
          marginTop: '12px',
          width: '100%',
          padding: '10px',
          backgroundColor: '#1A3E2A',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer'
        }}
      >
        Select Booking →
      </button>
    </div>
  );
}

// Service Card Component
function ServiceCard({ service, onSelect, isMobile }) {
  return (
    <div
      onClick={() => onSelect(service)}
      style={{
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '2px solid #E3DFD3',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#1A3E2A';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(26, 62, 42, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#E3DFD3';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '700', color: '#1A3E2A' }}>
        {service.name}
      </h4>
      {service.description && (
        <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#5F6B61', lineHeight: '1.4' }}>
          {service.description}
        </p>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '6px' : '0', marginTop: '12px' }}>
        <span style={{ fontSize: '14px', color: '#5F6B61' }}>
          {service.totalItems || 0} items available
        </span>
        <span style={{ fontSize: '16px', fontWeight: '700', color: '#1A3E2A' }}>
          ₹{(service.price || 0).toFixed(2)}
        </span>
      </div>
      <button
        style={{
          marginTop: '12px',
          width: '100%',
          padding: '10px',
          backgroundColor: '#1A3E2A',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer'
        }}
      >
        Select Service →
      </button>
    </div>
  );
}

// Item Card Component
function ItemCard({ item, quantity, onQuantityChange, isMobile }) {
  const stockStatus = item.quantity <= item.minStockLevel ? 'LOW' : 'IN_STOCK';
  
  return (
    <div style={{
      padding: '20px',
      backgroundColor: 'white',
      borderRadius: '12px',
      border: `2px solid ${quantity > 0 ? '#1A3E2A' : '#E3DFD3'}`,
      transition: 'all 0.2s ease'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px', gap: '8px' }}>
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '700', color: '#1A3E2A' }}>
            {item.name}
          </h4>
          <p style={{ margin: '0', fontSize: '12px', color: '#5F6B61' }}>
            SKU: {item.sku}
          </p>
        </div>
        <span style={{
          padding: '4px 8px',
          backgroundColor: stockStatus === 'LOW' ? '#C9903C' : '#1A3E2A',
          color: 'white',
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: '600',
          whiteSpace: 'nowrap'
        }}>
          {item.quantity} in stock
        </span>
      </div>

      {item.description && (
        <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#5F6B61', lineHeight: '1.4' }}>
          {item.description}
        </p>
      )}

      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '4px' : '0',
        marginBottom: '12px'
      }}>
        <span style={{ fontSize: '18px', fontWeight: '700', color: '#1A3E2A' }}>
          ₹{item.unitPrice.toFixed(2)}
        </span>
        <span style={{ fontSize: '12px', color: '#5F6B61' }}>
          per unit
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
        <button
          onClick={() => onQuantityChange(item, Math.max(0, quantity - 1))}
          disabled={quantity === 0}
          style={{
            width: isMobile ? '40px' : '36px',
            height: isMobile ? '40px' : '36px',
            backgroundColor: quantity === 0 ? '#EEEBE1' : '#1A3E2A',
            color: quantity === 0 ? '#A5A89B' : 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '18px',
            fontWeight: '700',
            cursor: quantity === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          −
        </button>
        <input
          type="number"
          value={quantity}
          onChange={(e) => onQuantityChange(item, e.target.value)}
          min="0"
          max={item.quantity}
          style={{
            flex: 1,
            minWidth: 0,
            padding: isMobile ? '10px 8px' : '10px',
            border: '2px solid #E3DFD3',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            textAlign: 'center'
          }}
        />
        <button
          onClick={() => onQuantityChange(item, Math.min(item.quantity, quantity + 1))}
          disabled={quantity >= item.quantity}
          style={{
            width: isMobile ? '40px' : '36px',
            height: isMobile ? '40px' : '36px',
            backgroundColor: quantity >= item.quantity ? '#EEEBE1' : '#1A3E2A',
            color: quantity >= item.quantity ? '#A5A89B' : 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '18px',
            fontWeight: '700',
            cursor: quantity >= item.quantity ? 'not-allowed' : 'pointer'
          }}
        >
          +
        </button>
      </div>

      {quantity > 0 && (
        <div style={{
          marginTop: '12px',
          padding: '10px',
          backgroundColor: '#F0F3EE',
          borderRadius: '6px',
          textAlign: 'center'
        }}>
          <span style={{ fontSize: '14px', color: '#0F2619', fontWeight: '600' }}>
            Subtotal: ₹{(quantity * item.unitPrice).toFixed(2)}
          </span>
        </div>
      )}
    </div>
  );
}

export default ServicesBooking;
